'use client';
import React, { useEffect, useId, useRef, useState } from 'react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { motion, AnimatePresence } from 'motion/react';
import { useOnClickOutside, useScrollLock } from 'usehooks-ts';
import { cn } from 'lib/utils';
import { trackEvent } from 'lib/analytics';
import { Bloom, Button, Icon, Link, Wrapper } from 'ui';
import { type GlobalHeaderProps } from './index';

interface GlobalHeaderFullProps extends GlobalHeaderProps {
  isSticky?: boolean;
}

const CONTACT_US_TRACKING_PROPS = {
  event: 'CTA_clicked',
  properties: {
    cta: 'Contact Us',
    location: 'global nav',
    path: '/contact',
    action: 'go to contact us',
  },
};

const GlobalHeaderFull = ({
  ctaLink,
  isSticky,
  items,
}: GlobalHeaderFullProps) => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelId = useId();

  // Use scroll lock hook to prevent background scrolling when menu is open
  const { lock: lockScroll, unlock: unlockScroll } = useScrollLock({
    autoLock: false,
    lockTarget: 'html',
  });

  const handleDropdownClick = (index: number) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  // Handle outside clicks
  useOnClickOutside(dropdownRef as React.RefObject<HTMLElement>, () =>
    closeDropdown()
  );

  // Handle escape key and window resize
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeDropdown !== null) {
        // Restore focus to toggle, then close
        toggleRefs.current[activeDropdown]?.focus();
        setActiveDropdown(null);
      }
    };

    const handleResize = () => {
      setActiveDropdown(null);
    };

    if (activeDropdown !== null) {
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeDropdown]);

  // Prevent body scroll when menu is open and scroll to top
  useEffect(() => {
    if (activeDropdown !== null) {
      // Smooth scroll to top when menu opens
      // window.scrollTo({
      //   top: 0,
      //   behavior: 'smooth',
      // });

      // Lock body scroll
      lockScroll();
    } else {
      // Unlock body scroll
      unlockScroll();
    }

    // Cleanup on unmount
    return () => {
      unlockScroll();
    };
  }, [activeDropdown, lockScroll, unlockScroll]);

  const activeItem = activeDropdown !== null ? items[activeDropdown] : null;
  const isDropdownOpen =
    activeDropdown !== null && activeItem && 'children' in activeItem;

  return (
    <>
      <div
        ref={dropdownRef}
        className="max-lg:hidden absolute left-0 top-full w-full h-header-height-full px-5"
      >
        {/* Main Nav */}
        <Wrapper
          as="nav"
          variant="header"
          role="navigation"
          aria-label="Main navigation"
          className={cn(
            'relative z-10 h-full flex items-center justify-between bg-graphite-900 transition-all duration-300',
            {
              'rounded-b-lg': isSticky && !isDropdownOpen,
            }
          )}
        >
          {/* Logo */}
          <NextLink
            href="/"
            className="flex rounded focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500"
            aria-label="Navigate to homepage"
            onClick={() => closeDropdown()}
          >
            <NextImage
              src="/_assets/header-logo-desktop.svg"
              alt="Oura for Organizations"
              width={225}
              height={25}
              className="w-56.25 h-6.25"
            />
          </NextLink>

          <ul className="flex items-center justify-end gap-8 ml-auto">
            {items.map((item, index) => (
              <li key={index}>
                {'href' in item ? (
                  // Simple link
                  <Link
                    href={item.href}
                    target={item.target}
                    rel={item.rel}
                    className="inline-flex items-center font-bold no-underline"
                  >
                    {item.text}
                  </Link>
                ) : (
                  // Dropdown button + panel (panel in DOM right after toggle for natural tab order)
                  <>
                    <Link
                      ref={(el) => {
                        toggleRefs.current[index] = el as HTMLButtonElement;
                      }}
                      onClick={() => handleDropdownClick(index)}
                      className="inline-flex items-center gap-1.5 font-bold no-underline"
                      aria-expanded={activeDropdown === index}
                      aria-haspopup="true"
                      aria-controls={
                        activeDropdown === index
                          ? `${panelId}-${index}`
                          : undefined
                      }
                    >
                      <span>{item.label}</span>
                      <Icon
                        aria-hidden="true"
                        name="chevron-down"
                        className={cn(
                          'h-2 w-auto shrink-0 transition-transform duration-200',
                          {
                            'scale-y-[-1]': activeDropdown === index,
                          }
                        )}
                      />
                    </Link>

                    {/* Dropdown Panel - rendered after toggle for natural tab order */}
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          id={`${panelId}-${index}`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{
                            opacity: { duration: 0.15 },
                            height: { duration: 0.3, ease: 'easeOut' },
                          }}
                          className="absolute left-0 top-full w-full overflow-hidden -ml-5"
                          style={{ width: 'calc(100% + 2.5rem)' }}
                        >
                          <motion.div
                            layout
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                            className="px-5"
                          >
                            <Wrapper
                              as="div"
                              variant="header"
                              className="relative z-10 py-16 bg-graphite-900 shadow-lg rounded-b-lg overflow-hidden"
                            >
                              <Bloom
                                positionX="left"
                                positionY="top"
                                variant="blue"
                              />
                              <Bloom
                                positionX="right"
                                positionY="bottom"
                                variant="blue"
                              />

                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={`content-${index}`}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 20 }}
                                  transition={{
                                    duration: 0.25,
                                    ease: 'easeOut',
                                  }}
                                  className="grid grid-cols-7 gap-15"
                                >
                                  {/* CTA Section - Left */}
                                  <div className="col-span-2">
                                    <p className="font-mono text-eyebrow-sm uppercase text-athletic-200 mb-3">
                                      {item.panelEyebrow}
                                    </p>
                                    <h3 className="text-p1 mb-2">
                                      {item.panelTitle}
                                    </h3>
                                    <p className="text-p2 text-neutral-200">
                                      {item.panelIntro}
                                    </p>
                                  </div>

                                  {/* Links Grid - Right */}
                                  <div className="col-span-5">
                                    <div className="grid grid-cols-3 gap-y-10 gap-x-13">
                                      {item.children.map(
                                        (child, childIndex) => (
                                          <motion.div
                                            key={`${index}-${childIndex}`}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                              duration: 0.2,
                                              delay: childIndex * 0.03,
                                            }}
                                          >
                                            <NextLink
                                              href={child.link.href}
                                              target={child.link.target}
                                              rel={child.link.rel}
                                              className="group block py-4 transition-colors no-underline text-clay-50 hover:text-athletic-200 focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500 focus-visible:rounded-sm border-t border-clay-50/20"
                                              onClick={() => {
                                                if (child.link.trackingProps) {
                                                  trackEvent(
                                                    child.link.trackingProps
                                                      .event,
                                                    child.link.trackingProps
                                                      .properties
                                                  );
                                                }
                                                closeDropdown();
                                              }}
                                            >
                                              <span className="flex flex-col gap-3">
                                                <span className="flex gap-2 items-start">
                                                  {child.icon && (
                                                    <span className="size-4.5 flex items-center justify-center shrink-0 mt-0.5">
                                                      <Icon
                                                        name={child.icon}
                                                        className="size-auto max-w-full max-h-full fill-clay-50 group-hover:fill-athletic-200"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  )}

                                                  <span className="block font-bold">
                                                    <span>
                                                      {child.link.text}
                                                    </span>
                                                    {child.link.target ===
                                                      '_blank' && (
                                                      <Icon
                                                        name="external-link-arrow"
                                                        className="align-middle inline-block w-2.5 h-auto fill-clay-50 group-hover:fill-athletic-200 ml-2"
                                                        aria-hidden="true"
                                                      />
                                                    )}
                                                  </span>
                                                </span>
                                                <span className="block text-p3 text-neutral-200">
                                                  {child.description}
                                                </span>
                                              </span>
                                            </NextLink>
                                          </motion.div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </motion.div>
                              </AnimatePresence>
                            </Wrapper>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </li>
            ))}

            <li onClick={() => closeDropdown()}>
              <Button
                href={ctaLink.href}
                trackingProps={CONTACT_US_TRACKING_PROPS}
              >
                {ctaLink.text}
              </Button>
            </li>
          </ul>
        </Wrapper>
      </div>

      {/* Blur Overlay */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-lg:hidden fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[5]"
            onClick={() => closeDropdown()}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalHeaderFull;
